import { useState } from 'react'
import { api } from '../api/client'
import type { ProposedChangesetFile } from '../api/client'
import {
  EXTRA_FILE_KINDS,
  KINDS_WITHOUT_NAME,
  normalizeName,
  pathFor,
  validateName,
  type ExtraFileKind,
} from './changesetKinds'

/**
 * ADR-134 D1 — compose a multi-file changeset in the workflow editor.
 *
 * A workflow is a node in a reference graph: crons drive it, skills route to it,
 * other activities call it. "Scan the board every 15 minutes and triage what you
 * find" is ONE change to two files — a discovery activity, and the cron repointed
 * at it. Until this existed the operator could only express that as a sequence of
 * individually-broken states, and the first of those silently overwrote the
 * workflow it was authored from.
 *
 * The primary activity (the slot in the URL) is NOT listed here — it is the main
 * editor above. This is everything else the change touches.
 *
 * ## Why the targets are PICKED and the content is PREFILLED
 *
 * The first version of this took a typed name and added the file with EMPTY
 * content. That is a silent clobber, and it was reachable on the very first
 * button: `crons.yaml` already exists for most agents, so "add the schedule that
 * drives this workflow" handed the operator a blank box, and proposing it
 * REPLACED the agent's whole cron file — deleting the jobs and comments already
 * there. Nothing catches that: the result is valid YAML with no orphaned
 * references, so it validates clean and merges.
 *
 * A blank box cannot tell you whether you are creating a file or destroying one.
 * So targets come from what actually exists, an existing file opens with its
 * CURRENT content, and each row says which it is.
 */

/** Sentinel for the "not in the list — create a new one" option. */
const CREATE_NEW = '\x00new'

/** Per-kind wording for the picker. A python step body is neither a workflow nor
 *  a skill, and calling it either in the empty-state text is how an operator
 *  concludes the picker is for something else. */
const NOUN: Record<ExtraFileKind, { one: string; many: string }> = {
  cron: { one: 'schedule', many: 'schedules' },
  activity: { one: 'workflow', many: 'other workflows' },
  skill: { one: 'skill', many: 'skills' },
  python: { one: 'python step body', many: 'python step bodies' },
  'skill-file': { one: 'file inside a skill', many: 'files inside skills' },
  resource: { one: 'shared resource', many: 'shared resources' },
  allowlist: { one: 'command allowlist', many: 'command allowlists' },
}

export interface ChangesetFilesProps {
  agent: string
  /** Everything in the change EXCEPT the primary activity being edited above. */
  files: ProposedChangesetFile[]
  onChange: (files: ProposedChangesetFile[]) => void
  /** The agent's existing activity names, for the workflow picker. */
  existingWorkflows?: string[]
  /** The agent's existing skill slugs, for the skill picker. */
  existingSkills?: string[]
  /** ADR-111 IA-8 — the agent's existing activities/*.py stems (no extension),
   *  so editing a step body starts from the real file instead of a blank box.
   *  Same reason the workflow and skill pickers exist: a blank box cannot tell
   *  you whether you are creating a file or destroying one. */
  existingPythonSteps?: string[]
  disabled?: boolean
}

export default function ChangesetFiles({
  agent,
  files,
  onChange,
  existingWorkflows = [],
  existingSkills = [],
  existingPythonSteps = [],
  disabled,
}: ChangesetFilesProps) {
  const [adding, setAdding] = useState<ExtraFileKind | null>(null)
  const [picked, setPicked] = useState('')
  const [newName, setNewName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const options =
    adding === 'skill' ? existingSkills
      : adding === 'python' ? existingPythonSteps
        : existingWorkflows
  const needsName = adding !== null && !KINDS_WITHOUT_NAME.has(adding)
  const creatingNew = picked === CREATE_NEW || (needsName && options.length === 0)

  function reset() {
    setAdding(null)
    setPicked('')
    setNewName('')
    setError(null)
  }

  async function add() {
    const kind = adding
    if (!kind) return

    let name = ''
    if (!KINDS_WITHOUT_NAME.has(kind)) {
      name = normalizeName(kind, creatingNew ? newName : picked)
      if (!name) {
        setError(creatingNew ? 'Name the new file.' : 'Choose one, or create a new file.')
        return
      }
      const nameError = validateName(kind, name)
      if (nameError) {
        setError(nameError)
        return
      }
    }

    const path = pathFor(kind, agent, name)
    if (files.some(f => f.path === path)) {
      // The server refuses a changeset naming one path twice, so catching it
      // here keeps the operator from discovering it after writing the content.
      setError('That file is already part of this change.')
      return
    }

    // Load the CURRENT content for a file that already exists. This is the whole
    // point: editing an existing file must start from what is in it, or Propose
    // replaces it with whatever the box happens to contain.
    let content = ''
    let existed = false
    if (!creatingNew) {
      setLoading(true)
      try {
        const f = await api.getFleetFile(path)
        if (f.found) {
          content = f.content
          existed = true
        }
      } catch {
        // A failed read must not silently become an empty file — that is the
        // clobber this exists to prevent. Refuse the add and say so.
        setLoading(false)
        setError('Could not read the current file. Not adding it — proposing an empty one would erase it.')
        return
      }
      setLoading(false)
    }

    onChange([...files, { path, content, op: 'write' }])
    // Remember which rows opened from an existing file, so the row can say so.
    if (existed) existingPaths.add(path)
    reset()
  }

  function update(i: number, patch: Partial<ProposedChangesetFile>) {
    onChange(files.map((f, n) => (n === i ? { ...f, ...patch } : f)))
  }

  return (
    <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-700">Also in this change</h4>
        <span className="text-xs text-slate-500">
          {files.length === 0 ? 'just this workflow' : `${files.length + 1} files, one PR`}
        </span>
      </div>

      <p className="mt-1 text-xs text-slate-600">
        A workflow is not edited alone: crons drive it, skills route to it, and a python
        task runs a step body next to it. Renaming or deleting one without its referrers
        orphans them, and the change will be refused naming the file it broke — add that
        file here instead.
      </p>

      {files.map((f, i) => (
        <div key={f.path} className="mt-3 rounded border border-slate-300 bg-white p-2">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <code className="block truncate text-xs text-slate-700">{f.path}</code>
              <span className="text-[10px] text-slate-500">
                {existingPaths.has(f.path) ? 'editing the existing file' : 'creating a new file'}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={f.op === 'delete'}
                  disabled={disabled}
                  onChange={e => update(i, { op: e.target.checked ? 'delete' : 'write' })}
                />
                delete
              </label>
              <button
                type="button"
                disabled={disabled}
                className="text-xs text-red-600 hover:underline"
                onClick={() => onChange(files.filter((_, n) => n !== i))}
              >
                remove
              </button>
            </div>
          </div>
          {f.op === 'delete' ? (
            <p className="mt-2 text-xs text-amber-700">
              This file will be deleted in the same PR. A rename is a new file here plus a
              delete of the old one — done together, so nothing ever sees the change
              half-applied.
            </p>
          ) : (
            <textarea
              className="mt-2 h-32 w-full rounded border border-slate-300 p-2 font-mono text-xs"
              value={f.content ?? ''}
              disabled={disabled}
              spellCheck={false}
              onChange={e => update(i, { content: e.target.value })}
              aria-label={f.path}
            />
          )}
        </div>
      ))}

      {adding ? (
        <div className="mt-3 rounded border border-indigo-200 bg-white p-2">
          <p className="text-xs text-slate-600">
            {EXTRA_FILE_KINDS.find(k => k.kind === adding)?.hint}
          </p>

          {needsName && (
            <>
              <select
                className="mt-2 w-full rounded border border-slate-300 px-2 py-1 text-sm"
                value={picked}
                disabled={disabled || options.length === 0}
                onChange={e => {
                  setPicked(e.target.value)
                  setError(null)
                }}
                aria-label={`choose a ${NOUN[adding].one}`}
              >
                <option value="">
                  {options.length === 0
                    ? `this agent has no ${NOUN[adding].many} yet`
                    : `choose an existing ${NOUN[adding].one}…`}
                </option>
                {options.map(o => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
                <option value={CREATE_NEW}>+ create a new one…</option>
              </select>
              {creatingNew && (
                <input
                  className="mt-2 w-full rounded border border-slate-300 px-2 py-1 text-sm"
                  placeholder={
                    adding === 'python' ? 'new step body name (e.g. collect_ga4)'
                      : `new ${NOUN[adding].one} name`
                  }
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  aria-label="new file name"
                />
              )}
            </>
          )}

          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              disabled={disabled || loading}
              className="rounded bg-indigo-600 px-2 py-1 text-xs text-white disabled:opacity-50"
              onClick={() => { void add() }}
            >
              {loading ? 'Loading current file…' : 'Add'}
            </button>
            <button type="button" className="text-xs text-slate-600 hover:underline" onClick={reset}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {EXTRA_FILE_KINDS.map(k => (
            <button
              key={k.kind}
              type="button"
              disabled={disabled}
              className="rounded border border-indigo-300 px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
              onClick={() => {
                setAdding(k.kind)
                setPicked('')
                setNewName('')
                setError(null)
              }}
            >
              + {k.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Paths this session opened from a file that already existed on the fleet.
 *
 * Module-level rather than component state because the row label must survive
 * the parent re-rendering this component with a new `files` array, which it does
 * on every keystroke in a textarea. Membership only ever grows within a page
 * load, and a stale entry's worst case is a row labelled "editing" for a file
 * that is in fact new — a cosmetic error, never a destructive one, because the
 * CONTENT is what was loaded either way.
 */
const existingPaths = new Set<string>()
